"use client"

import clsx from "clsx"
import { npPagePxClassName, npSectionMtClassName } from "src/components/np-design/components/constants"
import { NpCarouselArrow } from "src/components/np-design/components/NpCarouselArrow"
import { NpPaginationDots } from "src/components/np-design/components/NpPaginationDots"
import { NpHeading } from "src/components/np-design/components/NpSubtitle"
import { NpUppercaseHeading } from "src/components/np-design/components/NpUppercaseHeading"
import { Img } from "src/components/unstyled/Img"
import { CarouselSlideData } from "src/data/product-details/types"
import { useEmbla } from "src/utilities/embla"
import { getPlaceholderUrl, ImageCdnOptions, ImageFit, toImageCdnUrl } from "src/utilities/images"

const imageSizes = [1, 1.5, 2].map<ImageCdnOptions>((multiplier) => ({
  w: multiplier * 400,
  h: multiplier * 400,
  fit: "cover",
}))

const imageSizeMin = imageSizes.at(0)!
const imageSizeMax = imageSizes.at(-1)!

export type NpFeatureCarouselSectionProps = {
  capitalText?: string
  heading?: string
  slides: CarouselSlideData[]
  fit?: ImageFit
  aspectRatio?: `${string}/${string}`
}

export const NpFeatureCarouselSection = ({
  capitalText,
  heading,
  slides,
  fit,
  aspectRatio,
}: NpFeatureCarouselSectionProps) => {
  const { ref, embla, scrollSnaps, activeIndex, hasPrev, hasNext, justifyCenter } = useEmbla()

  fit ||= "cover"

  return (
    <section className={clsx(npSectionMtClassName, npPagePxClassName, "flex max-w-full flex-col items-center gap-6")}>
      {capitalText && <NpUppercaseHeading as={heading ? "p" : "h2"}>{capitalText}</NpUppercaseHeading>}
      {heading && <NpHeading as="h2">{heading}</NpHeading>}

      <div className="embla relative mx-auto mt-6 w-full max-w-320">
        <div
          ref={ref}
          className={clsx("embla__viewport overflow-hidden", {
            "rounded-3xl": !slides.some((slide) => slide.captionPrimary),
            "cursor-grab active:cursor-grabbing": hasPrev || hasNext,
          })}
        >
          <div
            className={clsx("embla__container flex max-h-full sm:-ml-4 lg:-ml-6 xl:-ml-10", {
              "justify-center": justifyCenter,
            })}
          >
            {slides.map((slide, index) => {
              const { captionPrimary, captionSecondary } = slide
              const rawSrc = slide.src || getPlaceholderUrl(imageSizeMax)
              const src = toImageCdnUrl(rawSrc, { ...imageSizeMin, fit })
              const srcSet = imageSizes
                .map((size) => `${toImageCdnUrl(rawSrc, { ...size, fit })} ${size.w}w`)
                .join(", ")
              const sizes = imageSizeMin.w + "px"

              return (
                <div
                  key={index}
                  className="embla__slide max-h-full min-w-0 shrink-0 grow-0 basis-full sm:basis-104 sm:pl-4 lg:basis-106 lg:pl-6 xl:basis-110 xl:pl-10"
                >
                  <div className="embla__slide_content flex w-full flex-col items-center gap-8">
                    <Img
                      src={src}
                      srcSet={srcSet}
                      sizes={sizes}
                      alt={captionPrimary || ""}
                      className={clsx("size-full rounded-3xl", fit === "cover" ? "object-cover" : "object-contain")}
                      style={{
                        aspectRatio: aspectRatio || "1/1",
                      }}
                    />

                    {captionPrimary && (
                      <div className="flex flex-col items-center gap-2 px-4 text-center leading-normal">
                        <div className="font-bold text-balance">{captionPrimary}</div>
                        {captionSecondary && <div className="text-balance">{captionSecondary}</div>}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {hasPrev && (
          <div className="absolute top-50 left-4 hidden -translate-y-1/2 md:block">
            <NpCarouselArrow side="left" onClick={() => embla?.scrollPrev()} />
          </div>
        )}
        {hasNext && (
          <div className="absolute top-50 right-4 hidden -translate-y-1/2 md:block">
            <NpCarouselArrow side="right" onClick={() => embla?.scrollNext()} />
          </div>
        )}
      </div>

      {!justifyCenter && <NpPaginationDots count={scrollSnaps.length} activeIndex={activeIndex} />}
    </section>
  )
}
